import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Modal,
  Input,
  Select,
} from 'antd';
import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformTenancyService } from '@/services/platformTenancy.service';

const { Title, Text, Paragraph } = Typography;

const statusColor: Record<string, string> = {
  ACTIVE: 'green',
  DRAFT: 'default',
  PROVISIONING: 'processing',
  FAILED: 'error',
  SUSPENDED: 'orange',
  READ_ONLY: 'gold',
  ARCHIVED: 'default',
};

const PlatformTenantDetailPage: React.FC = () => {
  const { tenantId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachCompanyId, setAttachCompanyId] = useState<string>();

  const tenantQ = useQuery({
    queryKey: ['platform-tenant', tenantId],
    queryFn: () => platformTenancyService.getTenant(tenantId),
    enabled: !!tenantId,
  });

  const runtimeQ = useQuery({
    queryKey: ['platform-runtime'],
    queryFn: () => platformTenancyService.getRuntime(),
  });

  const multiCompanyOn = !!runtimeQ.data?.multiCompanyCreationEnabled;

  const attachableQ = useQuery({
    queryKey: ['platform-attachable', tenantId],
    queryFn: () => platformTenancyService.listAttachableCompanies(tenantId),
    enabled: !!tenantId && attachOpen && multiCompanyOn,
  });

  const provQ = useQuery({
    queryKey: ['platform-tenant-prov', tenantId],
    queryFn: () => platformTenancyService.getProvisioning(tenantId),
    enabled: !!tenantId,
  });

  const auditQ = useQuery({
    queryKey: ['platform-tenant-audit', tenantId],
    queryFn: () => platformTenancyService.getAudit(tenantId),
    enabled: !!tenantId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['platform-tenant', tenantId] });
    qc.invalidateQueries({ queryKey: ['platform-tenant-prov', tenantId] });
    qc.invalidateQueries({ queryKey: ['platform-tenant-audit', tenantId] });
    qc.invalidateQueries({ queryKey: ['platform-tenants'] });
    qc.invalidateQueries({ queryKey: ['platform-attachable', tenantId] });
  };

  const attachM = useMutation({
    mutationFn: () => platformTenancyService.attachCompany(tenantId, attachCompanyId!),
    onSuccess: () => {
      message.success('Company attached to this tenant');
      setAttachOpen(false);
      setAttachCompanyId(undefined);
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Attach failed'),
  });

  const provisionM = useMutation({
    mutationFn: () => platformTenancyService.provision(tenantId),
    onSuccess: () => {
      message.success('Provisioning started');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Provisioning failed'),
  });
  const retryM = useMutation({
    mutationFn: () => platformTenancyService.retryProvision(tenantId),
    onSuccess: () => {
      message.success('Retry complete');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Retry failed'),
  });
  const rollbackM = useMutation({
    mutationFn: () => platformTenancyService.rollback(tenantId),
    onSuccess: () => {
      message.success('Draft rolled back');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Rollback failed'),
  });
  const activateM = useMutation({
    mutationFn: () => platformTenancyService.activate(tenantId, reason || undefined),
    onSuccess: () => {
      message.success('Activated');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Activate failed'),
  });
  const suspendM = useMutation({
    mutationFn: () => platformTenancyService.suspend(tenantId, reason || undefined),
    onSuccess: () => {
      message.success('Suspended');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Suspend failed'),
  });
  const archiveM = useMutation({
    mutationFn: () => platformTenancyService.archive(tenantId, reason || undefined),
    onSuccess: () => {
      message.success('Archived');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Archive failed'),
  });
  const diagM = useMutation({
    mutationFn: () => platformTenancyService.runDiagnostics(tenantId),
    onSuccess: (d: any) => {
      message[d?.ok ? 'success' : 'warning'](d?.summary || 'Diagnostics done');
      invalidate();
    },
    onError: (e: any) => message.error(e?.message || 'Diagnostics failed'),
  });

  const t = tenantQ.data as any;
  const steps = ((provQ.data as any)?.steps || []) as any[];

  const confirmAction = (title: string, onOk: () => void) => {
    Modal.confirm({
      title,
      content: (
        <Input.TextArea
          rows={2}
          placeholder="Reason (optional)"
          onChange={(e) => setReason(e.target.value)}
        />
      ),
      onOk,
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/platform/companies')}>
          Back
        </Button>
      </Space>

      {tenantQ.isLoading || !t ? (
        <Card loading={tenantQ.isLoading}>
          {!tenantQ.isLoading && <Text type="secondary">Tenant not found</Text>}
        </Card>
      ) : (
        <>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {t.display_name || t.name}
              </Title>
              <Space>
                <Text type="secondary">{t.tenant_code}</Text>
                <Tag color={statusColor[t.status] || 'default'}>{t.status}</Tag>
              </Space>
            </div>
            <Space wrap>
              {(['DRAFT', 'FAILED', 'PROVISIONING'].includes(t.status) ||
                (!t.primary_company_id && t.status !== 'ARCHIVED')) && (
                <Button type="primary" loading={provisionM.isPending} onClick={() => provisionM.mutate()}>
                  Provision
                </Button>
              )}
              {t.status === 'FAILED' && (
                <Button loading={retryM.isPending} onClick={() => retryM.mutate()}>
                  Retry
                </Button>
              )}
              {['DRAFT', 'FAILED', 'PROVISIONING'].includes(t.status) && !t.activated_at && (
                <Button danger loading={rollbackM.isPending} onClick={() => rollbackM.mutate()}>
                  Rollback draft
                </Button>
              )}
              {t.status !== 'ACTIVE' && !!t.primary_company_id && (
                <Button
                  onClick={() => confirmAction('Activate tenant?', () => activateM.mutate())}
                >
                  Activate
                </Button>
              )}
              {t.status === 'ACTIVE' && (
                <Button
                  onClick={() => confirmAction('Suspend tenant?', () => suspendM.mutate())}
                >
                  Suspend
                </Button>
              )}
              <Button loading={diagM.isPending} onClick={() => diagM.mutate()}>
                Run diagnostics
              </Button>
              <Button
                danger
                onClick={() => confirmAction('Archive tenant?', () => archiveM.mutate())}
              >
                Archive
              </Button>
            </Space>
          </Space>

          <Tabs
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: (
                  <Row gutter={16}>
                    <Col xs={24} lg={14}>
                      <Card title="Identity" size="small">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Primary company">
                            {t.primary_company_name || '—'} ({t.primary_company_code || 'n/a'})
                          </Descriptions.Item>
                          <Descriptions.Item label="Country">{t.country || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Domain hint">{t.domain_hint || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Vertical hint">{t.vertical_hint || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Contact">
                            {t.primary_contact_email || '—'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Plan">{t.plan_code || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Health">
                            {t.health_band || '—'}
                            {t.health_score != null ? ` (${t.health_score})` : ''}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} lg={10}>
                      <Card
                        title="Linked companies"
                        size="small"
                        style={{ marginBottom: 16 }}
                        extra={
                          multiCompanyOn ? (
                            <Button
                              size="small"
                              type="link"
                              icon={<LinkOutlined />}
                              onClick={() => setAttachOpen(true)}
                            >
                              Attach company
                            </Button>
                          ) : null
                        }
                      >
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="id"
                          dataSource={t.companies || []}
                          columns={[
                            { title: 'Name', dataIndex: 'name' },
                            { title: 'Code', dataIndex: 'code', width: 100 },
                            {
                              title: 'Primary',
                              dataIndex: 'is_primary',
                              width: 80,
                              render: (v: number) => (v ? 'Yes' : ''),
                            },
                          ]}
                        />
                        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                          {multiCompanyOn
                            ? 'Attach an existing ERP company to this tenant for multi-company / intercompany use. Creating a new Platform Company makes a separate tenant — attach it here (or use Finance → IC Affiliates).'
                            : 'Multi-company attach is off (set PLATFORM_MULTI_COMPANY_CREATION_ENABLED=1 and restart API).'}
                        </Paragraph>
                      </Card>
                      <Card title="Administrators" size="small">
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="id"
                          dataSource={t.administrators || []}
                          columns={[
                            { title: 'Name', dataIndex: 'full_name' },
                            { title: 'Email', dataIndex: 'email' },
                            { title: 'Invite', dataIndex: 'invite_status', width: 100 },
                          ]}
                        />
                      </Card>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'packs',
                label: 'Solution Packs',
                children: (
                  <Card size="small">
                    <Table
                      rowKey={(r: any) => r.pack_key || r.packKey || r.id || JSON.stringify(r)}
                      dataSource={t.installed_packs || []}
                      pagination={false}
                      columns={[
                        {
                          title: 'Pack',
                          render: (_: unknown, r: any) => r.pack_key || r.packKey || r.name || '—',
                        },
                        {
                          title: 'Status',
                          render: (_: unknown, r: any) => r.status || r.install_status || 'installed',
                        },
                      ]}
                      locale={{ emptyText: 'No packs installed yet' }}
                    />
                  </Card>
                ),
              },
              {
                key: 'provisioning',
                label: 'Provisioning',
                children: (
                  <Card size="small">
                    <Table
                      rowKey="id"
                      dataSource={steps}
                      pagination={false}
                      columns={[
                        { title: 'Step', dataIndex: 'step_key' },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          width: 120,
                          render: (s: string) => (
                            <Tag
                              color={
                                s === 'SUCCEEDED'
                                  ? 'green'
                                  : s === 'FAILED'
                                    ? 'red'
                                    : s === 'SKIPPED'
                                      ? 'default'
                                      : 'processing'
                              }
                            >
                              {s}
                            </Tag>
                          ),
                        },
                        { title: 'Attempts', dataIndex: 'attempt_count', width: 90 },
                        {
                          title: 'Error',
                          dataIndex: 'error_text',
                          ellipsis: true,
                        },
                      ]}
                      locale={{ emptyText: 'No provisioning runs yet' }}
                    />
                  </Card>
                ),
              },
              {
                key: 'branches',
                label: 'Branches',
                children: (
                  <Card size="small">
                    <Table
                      rowKey="id"
                      dataSource={t.branches || []}
                      pagination={false}
                      columns={[
                        { title: 'Name', dataIndex: 'name' },
                        { title: 'Code', dataIndex: 'code', width: 100 },
                        {
                          title: 'HO',
                          dataIndex: 'is_head_office',
                          width: 60,
                          render: (v: number) => (v ? 'Yes' : ''),
                        },
                      ]}
                    />
                  </Card>
                ),
              },
              {
                key: 'subscription',
                label: 'Subscription',
                children: (
                  <Card size="small">
                    <Paragraph type="secondary">Wave 5 entitlement engine stub</Paragraph>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Plan">{t.plan_code || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Status">
                        {t.subscription_status || '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
              {
                key: 'usage',
                label: 'Usage',
                children: (
                  <Card size="small">
                    <Text type="secondary">Wave 5 usage metering stub — no live meters yet.</Text>
                  </Card>
                ),
              },
              {
                key: 'health',
                label: 'Health',
                children: (
                  <Card size="small">
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Band">{t.health_band || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Score">{t.health_score ?? '—'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
              {
                key: 'audit',
                label: 'Audit',
                children: (
                  <Card size="small">
                    <Table
                      rowKey="id"
                      loading={auditQ.isLoading}
                      dataSource={(auditQ.data as any[]) || []}
                      pagination={{ pageSize: 20 }}
                      columns={[
                        { title: 'When', dataIndex: 'created_at', width: 180 },
                        { title: 'Action', dataIndex: 'action' },
                        { title: 'Reason', dataIndex: 'reason', ellipsis: true },
                      ]}
                    />
                  </Card>
                ),
              },
              {
                key: 'support',
                label: 'Support',
                children: (
                  <Card size="small">
                    <Text type="secondary">
                      Support impersonation sessions are stubbed for Wave 7+.
                    </Text>
                  </Card>
                ),
              },
            ]}
          />

          <Modal
            title="Attach company to this tenant"
            open={attachOpen}
            onCancel={() => {
              setAttachOpen(false);
              setAttachCompanyId(undefined);
            }}
            onOk={() => {
              if (!attachCompanyId) {
                message.warning('Select a company');
                return;
              }
              attachM.mutate();
            }}
            confirmLoading={attachM.isPending}
            okText="Attach"
          >
            <Paragraph type="secondary">
              Moves the company onto this tenant (removes it from its previous tenant membership). Use this to
              group Smoke-EPC-2-related under Smoke EPC msehl5dq for intercompany.
            </Paragraph>
            <Select
              style={{ width: '100%' }}
              placeholder="Select company"
              showSearch
              optionFilterProp="label"
              loading={attachableQ.isLoading}
              value={attachCompanyId}
              onChange={setAttachCompanyId}
              options={(attachableQ.data || []).map((c) => ({
                value: c.id,
                label: `${c.code} — ${c.name}`,
              }))}
              notFoundContent={
                attachableQ.isLoading ? 'Loading…' : 'No other active companies available to attach'
              }
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default PlatformTenantDetailPage;
